import {
  compareHealthData,
  DEFAULT_PLAN_KEY,
  getHealthPlanActionData,
  getLinkedEntryIdFromAction,
  getMappedCriticality,
  isErrorInfo,
} from 'features/fde'
import { getLowestHealthScore, type HealthScoreData } from 'features/fde'
import isEmpty from 'lodash/isEmpty'
import isNaN from 'lodash/isNaN'
import isNil from 'lodash/isNil'
import { useMemo } from 'react'
import {
  type CentralizedHealthScore,
  type DamageInfoWithHealthCondition,
  NoRLHealthScore,
} from 'types'
import {
  DamageCategory,
  FDECustomerUnit,
  GeckoAssetHealthAction,
  GeckoAssetHealthPlan,
  GeckoAssetModel,
  GeckoColorScaleThreshold,
  GeckoComponent,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoComponentDefaults,
  GeckoEntry,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMarkupLayer,
  GeckoPublishingState,
  GeckoSiteConfig,
  GeckoUnit,
  RolodexEntryType,
} from 'types'
import { hasAtLeastOne } from 'utils/hasAtLeastOne'
import { SortingPlacement } from 'utils/sortingHelpers'

import { useFetchBulkHealthDataWithAllAssets } from './api/fetchHealthData'
import {
  NeededDataFetch,
  useFetchNeededSiteDataFromRolodex,
} from './api/fetchRolodexData'
import { listYearsToSimTo } from './utils/numYearsAbleToSimTo'

export type AhpPlanActionAndCost = {
  plan: GeckoAssetHealthPlan
  action: GeckoAssetHealthAction
  cost: number | null
}

export type AggregateUnitData = {
  unitSpecs: {
    unitData: GeckoUnit
    assetModel: GeckoAssetModel | undefined
    childComponents: ReadonlyArray<GeckoComponent>
    mappedCriticality: string
    kestrelModelId: string | undefined
  }
  mostRecentInspectionData: {
    inspection: GeckoInspection | undefined
    relevantCDF: GeckoComponentDataFrame | undefined
    relevantColorThreshold: GeckoColorScaleThreshold | undefined
  }
  health: {
    [year: number]: {
      riskScore: number | undefined
      minRLFromYear: number | undefined
      percentBelowTMin: number | undefined
      unitHealthScoreForYear: CentralizedHealthScore
      damageAreas:
        | undefined
        | {
            [componentId: string]: {
              damageAreas: DamageCategory[] | undefined
              riskScore: number | undefined
              healthScore: number | undefined
              componentHealthCondition: CentralizedHealthScore
              minRl: number | undefined
              percentBelowTmin: number | undefined
            }
          }
    }
  }
  allPlanCosts: ReadonlyArray<AhpPlanActionAndCost>
}

export type AggregateDataFetch =
  | {
      isMainLoading: false
      aggregateSiteData: ReadonlyArray<AggregateUnitData>
      isRlCalcLoading: boolean
    }
  | {
      isMainLoading: true
      aggregateSiteData: undefined
      isRlCalcLoading: boolean
    }

/**
 * Aggregates all the data needed for the SiteView page.
 */
export function useAggregateSiteData(siteID: string): AggregateDataFetch {
  const rolodexFetch: NeededDataFetch =
    useFetchNeededSiteDataFromRolodex(siteID)

  const { isLoading: minRLsFetchIsLoading, data: bulkHealthScores } =
    useFetchBulkHealthDataWithAllAssets(siteID)

  const aggregateData = useMemo(() => {
    if (rolodexFetch.isLoading) {
      return undefined
    }

    return rolodexFetch.allUnitsAtSite.map((curUnit) => {
      const componentsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const inspectionsInUnit = filterEntriesWithTags(
        rolodexFetch.allInspectionsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const markupLayersInUnit = filterEntriesWithTags(
        rolodexFetch.allMarkupLayersAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const healthPlanActionsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentHealthPlanActionsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const healthPlansInUnit =
        rolodexFetch.allComponentHealthPlansAtSite.filter((chp) => {
          return healthPlanActionsInUnit.some(
            (chpa) =>
              chpa.tags[RolodexEntryType.COMPONENT_HEALTH_PLAN] === chp.id
          )
        })

      const healthPlanActionSpecsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentHealthPlanActionSpecsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDatasetsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDatasetsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDataFramesInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDataFramesAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const componentDefaultsInUnit = filterEntriesWithTags(
        rolodexFetch.allComponentDefaultsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const assetModel = rolodexFetch.allAssetModelsAtSite.find(
        (curAssetModel) =>
          curAssetModel.tags[RolodexEntryType.UNIT] === curUnit.id
      )

      const fdeCustomerUnit = rolodexFetch.allFDECustomerUnitsAtSite.find(
        (curFDECustomerUnit) =>
          curFDECustomerUnit.tags[RolodexEntryType.UNIT] === curUnit.id
      )

      const kestrelModelsInUnit = filterEntriesWithTags(
        rolodexFetch.allKestrelModelsAtSite,
        RolodexEntryType.UNIT,
        curUnit.id
      )

      const idsToLookup =
        componentsInUnit.length > 0
          ? componentsInUnit.map((c) => c.id)
          : [curUnit.id]

      const preferredPlanId =
        healthPlansInUnit.find((hp) => hp.data.preferred)?.id ??
        DEFAULT_PLAN_KEY

      const damageInfoForUnit = isNil(bulkHealthScores)
        ? undefined
        : idsToLookup.reduce<
            ReadonlyArray<[string, DamageInfoWithHealthCondition]>
          >((acc, lookupId) => {
            const healthData = bulkHealthScores[lookupId]
            if (isNil(healthData)) {
              return acc
            }

            const planInfo = healthData[preferredPlanId]

            if (isNil(planInfo) || isErrorInfo(planInfo)) {
              return acc
            }

            return [...acc, [lookupId, planInfo]]
          }, [])

      const minRlsForUnit = damageInfoForUnit?.reduce<{
        [componentId: string]: {
          [year: number]:
            | NonNullable<AggregateUnitData['health'][number]>
            | undefined
        }
      }>((agg, [curComponentId, curDamageInfo]) => {
        if (agg[curComponentId]) {
          throw new Error(`Duplicate component ID found: ${curComponentId}`)
        }

        const yearlyInfo = Object.entries(curDamageInfo).reduce<{
          [year: number]:
            | NonNullable<AggregateUnitData['health'][number]>
            | undefined
        }>(
          (
            yearAgg,
            [year, yearData]: [string, DamageInfoWithHealthCondition[string]]
          ) => {
            const yearAsNumber = parseInt(year)

            if (isNaN(yearAsNumber)) {
              throw new Error(`Invalid year: ${year}`)
            }

            yearAgg[yearAsNumber] = {
              riskScore: yearData.riskScore,
              minRLFromYear: yearData.minRl,
              percentBelowTMin: yearData.percentBelowTmin,
              damageAreas: {
                [curComponentId]: {
                  riskScore: yearData.riskScore,
                  healthScore: yearData.healthScore,
                  damageAreas: yearData.damageAreas,
                  componentHealthCondition: yearData.componentHealthCondition,
                  minRl: yearData.minRl,
                  percentBelowTmin: yearData.percentBelowTmin,
                },
              },
              unitHealthScoreForYear: yearData.componentHealthCondition,
            }
            return yearAgg
          },
          {}
        )

        return { ...agg, [curComponentId]: yearlyInfo }
      }, {})

      return assembleUnitData({
        unit: curUnit,
        siteConfig: rolodexFetch.siteConfig,
        componentsInUnit,
        inspectionsInUnit,
        markupLayersInUnit,
        healthPlansInUnit: healthPlansInUnit,
        healthPlanActionsInUnit: healthPlanActionsInUnit,
        healthPlanActionSpecsInUnit: healthPlanActionSpecsInUnit,
        componentDatasetsInUnit,
        componentDataFramesInUnit,
        componentDefaultsInUnit,
        assetModel,
        fdeCustomerUnit,
        minRLsData: minRlsForUnit,
        kestrelModelId: kestrelModelsInUnit.at(0)?.id,
      })
    })
  }, [rolodexFetch, bulkHealthScores])

  return useMemo(() => {
    if (rolodexFetch.isLoading) {
      return {
        isMainLoading: true,
        aggregateSiteData: undefined,
        isRlCalcLoading: minRLsFetchIsLoading,
      }
    }

    return {
      isMainLoading: false,
      aggregateSiteData: aggregateData ?? [],
      isRlCalcLoading: minRLsFetchIsLoading,
    }
  }, [rolodexFetch, aggregateData, minRLsFetchIsLoading])
}

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/** Calculates and organizes all the asset-health-planning costs for a given Unit, broken down by Component */
function calculateAllPlanCostsInUnit(props: {
  componentsInUnit: ReadonlyArray<GeckoComponent>
  markupLayersInUnit: ReadonlyArray<GeckoMarkupLayer>
  healthPlansInUnit: ReadonlyArray<GeckoAssetHealthPlan>
  healthPlanActionsInUnit: ReadonlyArray<GeckoAssetHealthAction>
  healthPlanActionSpecsInUnit: ReadonlyArray<GeckoEntry>
  assetModel: GeckoAssetModel | undefined
  fdeCustomerUnit: GeckoEntryBase<FDECustomerUnit> | undefined
}): ReadonlyArray<AhpPlanActionAndCost> {
  const {
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    assetModel,
    fdeCustomerUnit,
  } = props

  const preferredHealthPlans = healthPlansInUnit.filter(
    (hp) => hp.data.preferred
  )

  return preferredHealthPlans.flatMap((curPlan) => {
    const planActions = filterEntriesWithTags(
      healthPlanActionsInUnit,
      RolodexEntryType.COMPONENT_HEALTH_PLAN,
      curPlan.id
    )

    return planActions.map((curAction) => {
      const linkedEntryId = getLinkedEntryIdFromAction(curAction)
      const actionSpec = healthPlanActionSpecsInUnit.find(
        (curSpec) => curSpec.id === linkedEntryId
      )

      const actionComponent = componentsInUnit.find(
        ({ id }) => curAction.tags[RolodexEntryType.COMPONENT] === id
      )

      const planActionData = getHealthPlanActionData({
        ahpAction: curAction,
        linkedEntry: actionSpec,
        markupLayers: markupLayersInUnit,
        selectedComponent: actionComponent,
        selectedComponentModel: actionComponent
          ? assetModel?.data.components.get(actionComponent.id)
          : undefined,
        fdeCustomerUnit: fdeCustomerUnit,
      })

      return {
        plan: curPlan,
        action: curAction,
        cost: planActionData.costTotal,
      }
    })
  })
}

// --------------------------------------------------------------------------------------------------------------------
// Utility Functions
/** Assembles all the site-view-data for a unit into an easily digestible format */
function assembleUnitData(props: {
  unit: GeckoUnit
  siteConfig: GeckoSiteConfig | undefined
  componentsInUnit: ReadonlyArray<GeckoComponent>
  inspectionsInUnit: ReadonlyArray<GeckoInspection>
  componentDatasetsInUnit: ReadonlyArray<GeckoComponentDataset>
  componentDataFramesInUnit: ReadonlyArray<GeckoComponentDataFrame>
  componentDefaultsInUnit: ReadonlyArray<GeckoComponentDefaults>
  markupLayersInUnit: ReadonlyArray<GeckoMarkupLayer>
  healthPlansInUnit: ReadonlyArray<GeckoAssetHealthPlan>
  healthPlanActionsInUnit: ReadonlyArray<GeckoAssetHealthAction>
  healthPlanActionSpecsInUnit: ReadonlyArray<GeckoEntry>
  assetModel: GeckoAssetModel | undefined
  fdeCustomerUnit: GeckoEntryBase<FDECustomerUnit> | undefined
  kestrelModelId: string | undefined
  minRLsData:
    | {
        [componentID: string]: {
          [year: number]:
            | NonNullable<AggregateUnitData['health'][number]>
            | undefined
        }
      }
    | undefined
}): AggregateUnitData {
  const {
    unit,
    siteConfig,
    inspectionsInUnit,
    componentDatasetsInUnit,
    componentDataFramesInUnit,
    componentDefaultsInUnit,
    assetModel,
    fdeCustomerUnit,
    kestrelModelId,
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    minRLsData,
  } = props

  // - - - -
  // Most recent inspection

  const mostRecentInspection = inspectionsInUnit
    .toSorted((a, b) => {
      return a.data.date < b.data.date ? 1 : -1
    })
    .at(0)

  const validCDSs = componentDatasetsInUnit.filter(
    (cds) => cds.data.publishing_state === GeckoPublishingState.Finalized
  )

  const relevantCDF = componentDataFramesInUnit
    .toSorted(GeckoComponentDataFrame.GetDefaultSort(validCDSs))
    .find(
      (cdf) =>
        // Find a CDF matching this unit and inspection
        cdf.tags.unit === unit.id &&
        cdf.tags.inspection === mostRecentInspection?.id
    )

  const relevantComponent = componentsInUnit.find(
    (c) => c.id === relevantCDF?.tags.component
  )

  const relevantColorThreshold = componentDefaultsInUnit
    .find((cd) => cd.tags.component === relevantComponent?.id)
    ?.data.color_scale_thresholds.get(relevantCDF?.data.params.view.id ?? '')

  // - - -
  // Plan Costs

  const allPlanCosts = calculateAllPlanCostsInUnit({
    componentsInUnit,
    markupLayersInUnit,
    healthPlansInUnit,
    healthPlanActionsInUnit,
    healthPlanActionSpecsInUnit,
    assetModel,
    fdeCustomerUnit,
  })

  // - - -
  // Health Score

  const criticality = unit.data.custom_fields?.find(
    (cf) => cf.fieldName === 'criticality'
  )?.inputValue

  // For each year, get the minimum RL across all components (with ties broken by the component with the highest percent
  // below Tmin), and aggregate the damage areas
  const healthStatsBase = !isEmpty(minRLsData)
    ? Object.values(minRLsData)
    : listYearsToSimTo().map((year) => ({ [year]: undefined }))
  const minHealthStats = healthStatsBase.reduce<AggregateUnitData['health']>(
    (aggStats, componentYearlyData) => {
      Object.entries(componentYearlyData).forEach(([year, yearData]) => {
        const yearAsNumber = parseInt(year)
        if (isNaN(yearAsNumber)) {
          throw new Error(`Invalid year: ${year}`)
        }

        if (isNil(yearData)) {
          // No health data - use fallback
          const fallbackHealthScore = mostRecentInspection
            ? NoRLHealthScore.NotEnoughData
            : NoRLHealthScore.NoInspection

          aggStats[yearAsNumber] = {
            riskScore: undefined,
            minRLFromYear: undefined,
            percentBelowTMin: undefined,
            damageAreas: {},
            unitHealthScoreForYear: fallbackHealthScore,
          }
          return
        }

        if (isNil(aggStats[yearAsNumber])) {
          aggStats[yearAsNumber] = yearData
          return
        }

        // Compare existing vs new data to determine which has worse health
        const existingData = aggStats[yearAsNumber]
        const comparator = compareHealthData(
          {
            minRLFromYear: existingData.minRLFromYear,
            percentBelowTMin: existingData.percentBelowTMin,
            hasInspection: !!mostRecentInspection,
            healthConditionFromYear: existingData.unitHealthScoreForYear,
            riskScore: existingData.riskScore,
          },
          {
            minRLFromYear: yearData.minRLFromYear,
            percentBelowTMin: yearData.percentBelowTMin,
            hasInspection: !!mostRecentInspection,
            healthConditionFromYear: yearData.unitHealthScoreForYear,
            riskScore: yearData.riskScore,
          },
          SortingPlacement.After
        )

        const mergedDamageAreas = {
          ...existingData.damageAreas,
          ...yearData.damageAreas,
        }

        const finalYearData =
          comparator < 0
            ? {
                // Existing data has lower RL (worse), keep it but merge damage areas
                ...existingData,
                damageAreas: mergedDamageAreas,
              }
            : {
                // New data has lower RL (worse), use it but merge damage areas
                ...yearData,
                damageAreas: mergedDamageAreas,
              }

        const healthScoreDataArray: HealthScoreData[] = Object.values(
          finalYearData.damageAreas ?? {}
        ).map((damageArea) => ({
          healthConditionFromYear: damageArea.componentHealthCondition,
          minRLFromYear: damageArea.minRl,
          percentBelowTMin: damageArea.percentBelowTmin,
          hasInspection: !!mostRecentInspection,
          riskScore: damageArea.riskScore,
        }))

        const unitHealthScoreForYear = hasAtLeastOne(healthScoreDataArray)
          ? getLowestHealthScore(healthScoreDataArray)
          : mostRecentInspection
            ? NoRLHealthScore.NotEnoughData
            : NoRLHealthScore.NoInspection

        aggStats[yearAsNumber] = {
          ...finalYearData,
          unitHealthScoreForYear,
        }
      })

      return aggStats
    },
    {}
  )

  const mappedCriticality = getMappedCriticality(
    criticality,
    siteConfig?.data.site_level_criticality_mapping
  )

  return {
    unitSpecs: {
      unitData: unit,
      assetModel: assetModel,
      childComponents: componentsInUnit,
      mappedCriticality,
      kestrelModelId,
    },
    mostRecentInspectionData: {
      inspection: mostRecentInspection,
      relevantCDF: relevantCDF,
      relevantColorThreshold,
    },
    allPlanCosts: allPlanCosts,
    health: minHealthStats,
  }
}
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/** filter a set of entries by a given tag */
function filterEntriesWithTags<T extends { tags: Record<string, unknown> }>(
  entries: ReadonlyArray<T>,
  tagKey: string,
  valueToCompare: string | undefined
): ReadonlyArray<T> {
  return entries.filter((curEntry) => curEntry.tags[tagKey] === valueToCompare)
}
